import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { selectAuth } from "modules/auth/redux/selectors";
import { useI18n } from "i18n";
import {
  deleteEventApi,
  reopenEventApi,
  stopEventApi,
} from "services/apiService/apis";
import { selectEvents } from "../selectors";
import useNotificationActions from "../../notification/hooks/useNotificationActions";
import {
  openUnlimitedEvent,
  removeEvent,
  removeUnlimitedEvent,
} from "../eventSlice";

interface UseEventAdmin {
  amIAdminForEvent: (eventId: string, userId: string) => boolean;
  deleteEvent: (eventId: string) => Promise<void>;
  stopEvent: (eventId: string) => Promise<void>;
  reopenEvent: (eventId: string) => Promise<void>;
}

const useEventAdmin = (): UseEventAdmin => {
  const allEvents = useSelector(selectEvents);
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const dispatch = useAppDispatch();
  const { isEventHelper } = useSelector(selectAuth);

  const { t } = useI18n(["common", "event"]);

  /**
   * Checks if current user is admin for the event
   * @param eventId
   */
  // TODO convert to util fn
  const amIAdminForEvent = (eventId: string, userId: string) => {
    const event = allEvents[eventId];

    if (event) {
      const admins = event.managers || event.admins;

      return admins?.includes(userId) ?? false;
    }

    return false;
  };

  const deleteEvent = async (eventId: string) => {
    try {
      if (isEventHelper) {
        addErrorNotification({
          message: t("event:permission.denied"),
        });

        return;
      }
      await deleteEventApi(eventId);
      addSuccessNotification({
        message: t("event:delete.event.success"),
      });
      dispatch(removeEvent(eventId));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : undefined;

      addErrorNotification({
        message: errorMessage || t("event:delete.event.error"),
      });
    }
  };
  const stopEvent = async (eventId: string) => {
    try {
      if (isEventHelper) {
        addErrorNotification({
          message: t("event:permission.denied.stop"),
        });

        return;
      }
      const { event } = await stopEventApi(eventId);
      addSuccessNotification({
        message: t("event:close.space.success"),
      });
      if (event) {
        dispatch(removeUnlimitedEvent(event));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : undefined;

      addErrorNotification({
        message: errorMessage || t("event:close.event.error"),
      });
    }
  };

  const reopenEvent = async (eventId: string) => {
    try {
      if (isEventHelper) {
        addErrorNotification({
          message: t("event:permission.denied.reopen"),
        });

        return;
      }
      const { event } = await reopenEventApi(eventId);
      addSuccessNotification({
        message: t("event:reopen.space.success"),
      });
      if (event) {
        dispatch(openUnlimitedEvent(event));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : undefined;

      addErrorNotification({
        message: errorMessage || t("event:reopen.event.error"),
      });
    }
  };

  return {
    amIAdminForEvent,
    deleteEvent,
    stopEvent,
    reopenEvent,
  };
};

export default useEventAdmin;
